import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Trash, FileXls, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { STATUSES, formatApiError, hasRole } from "../lib/api";
import { StatusBadge, AgingBadge } from "../components/badges";
import { useAuth } from "../context/AuthContext";
import { EditOrderDialog } from "../components/EditOrderDialog";
import { OrderItemsTable } from "../components/order/OrderItemsTable";
import { DispatchDetailsCard } from "../components/order/DispatchDetailsCard";
import { DispatchPanel } from "../components/order/DispatchPanel";
import { WhatsAppSharePanel } from "../components/order/WhatsAppSharePanel";
import { StatusUpdatePanel } from "../components/order/StatusUpdatePanel";
import { OrderTimeline } from "../components/order/OrderTimeline";

const emptyDispatch = { dispatch_date: "", transporter_name: "", vehicle_number: "", lr_number: "", driver_mobile: "" };
const withKey = (it) => ({ _key: crypto.randomUUID(), product_name: it.product_name, quantity: it.quantity });

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [targetStatus, setTargetStatus] = useState("");
  const [note, setNote] = useState("");
  const [dispatch, setDispatch] = useState(emptyDispatch);
  const [dispatchItems, setDispatchItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then(({ data }) => {
      setOrder(data);
      if (data.dispatch) setDispatch({ ...emptyDispatch, ...data.dispatch });
      const source = data.dispatch_items?.length ? data.dispatch_items : data.items;
      setDispatchItems(source.map(withKey));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!order) return <div className="text-sm text-slate-500">Loading order…</div>;

  const canUpdate = hasRole(user, "production", "dispatch", "admin");
  const canEdit = (hasRole(user, "admin") || (hasRole(user, "sales", "sales_lead") && (order.created_by_id === user.id || order.created_by === user.name)))
    && !["dispatched", "delivered"].includes(order.status);
  const canDispatch = hasRole(user, "dispatch", "admin") && !["dispatched", "delivered"].includes(order.status);
  const needsDispatchForm = targetStatus === "dispatched";
  const currentIdx = STATUSES.findIndex((s) => s.value === order.status);

  const setDispatchItem = (i, key, value) =>
    setDispatchItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));

  const doDispatch = async () => {
    const parsed = dispatchItems.map((it) => ({
      product_name: String(it.product_name).trim(),
      quantity: parseFloat(it.quantity),
    }));
    if (parsed.some((it) => !it.product_name || !(it.quantity > 0))) {
      toast.error("Every dispatch line needs a product name and quantity > 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${id}/dispatch`, { items: parsed, dispatch });
      setOrder(data);
      toast.success(`Order ${data.order_number} dispatched`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async () => {
    if (!targetStatus) return;
    setSaving(true);
    try {
      const body = { status: targetStatus, note: note || null };
      if (needsDispatchForm) body.dispatch = dispatch;
      const { data } = await api.patch(`/orders/${id}/status`, body);
      setOrder(data);
      setTargetStatus("");
      setNote("");
      toast.success(`Status updated to ${data.status_label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveDispatch = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/orders/${id}/dispatch`, dispatch);
      setOrder(data);
      toast.success("Dispatch details saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const download = async (path, filename, okMsg, errMsg) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(okMsg);
    } catch (e) {
      toast.error(errMsg);
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/orders/${id}`);
      toast.success(`Order ${order.order_number} deleted${data.renumbered ? " — sequence renumbered" : ""}`);
      navigate("/orders");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="order-detail-page" className="max-w-5xl space-y-6">
      <Link to="/orders" data-testid="back-to-orders-link"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors duration-150 hover:text-slate-900">
        <ArrowLeft size={15} /> Back to orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-mono text-2xl font-bold text-slate-900 md:text-3xl" data-testid="order-number-heading">{order.order_number}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {order.customer_name} — {order.location} · Created by {order.created_by} on {order.created_at.slice(0, 10)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={order.status} label={order.status_label} />
          <AgingBadge color={order.aging_color} days={order.age_days} />
          {canEdit && (
            <button data-testid="edit-order-button" onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
              <PencilSimple size={15} weight="duotone" /> Edit
            </button>
          )}
          <button data-testid="print-slip-button"
            onClick={() => download(`/orders/${id}/slip`, `${order.order_number}_dispatch_slip.pdf`, "Dispatch slip downloaded", "Could not generate dispatch slip")}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            <Printer size={15} weight="duotone" /> Export PDF
          </button>
          <button data-testid="export-order-excel-button"
            onClick={() => download(`/orders/${id}/export/excel`, `${order.order_number}_order.xlsx`, "Order exported to Excel", "Could not export order")}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            <FileXls size={15} weight="duotone" /> Export Excel
          </button>
          {hasRole(user, "admin") && (
            <button data-testid="delete-order-detail-button" onClick={deleteOrder}
              className="flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition-colors duration-150 hover:bg-red-50">
              <Trash size={15} weight="duotone" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Progress steps */}
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-slate-200 bg-slate-50 p-4">
        {STATUSES.map((s, i) => (
          <div key={s.value} className="flex items-center gap-1">
            <span className={`rounded-md px-2.5 py-1 text-xs font-semibold ${i <= currentIdx ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-400"}`}>
              {i + 1}. {s.label}
            </span>
            {i < STATUSES.length - 1 && <span className="text-slate-300">—</span>}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <OrderItemsTable order={order} />
          <DispatchDetailsCard order={order} canUpdate={canUpdate} dispatch={dispatch}
            setDispatch={setDispatch} saveDispatch={saveDispatch} saving={saving} />
        </div>

        <div className="space-y-6">
          {canDispatch && (
            <DispatchPanel items={dispatchItems} setItem={setDispatchItem}
              addItem={() => setDispatchItems((p) => [...p, withKey({ product_name: "", quantity: "" })])}
              removeItem={(i) => setDispatchItems((p) => p.filter((_, idx) => idx !== i))}
              products={products} isAdmin={hasRole(user, "admin")}
              onDispatch={doDispatch} saving={saving} />
          )}
          {canUpdate && <WhatsAppSharePanel order={order} />}
          {canUpdate && (
            <StatusUpdatePanel order={order} targetStatus={targetStatus} setTargetStatus={setTargetStatus}
              note={note} setNote={setNote} updateStatus={updateStatus} saving={saving}
              needsDispatchForm={needsDispatchForm} />
          )}
          <OrderTimeline order={order} />
        </div>
      </div>

      {editing && (
        <EditOrderDialog order={order} products={products} isAdmin={hasRole(user, "admin")}
          onClose={() => setEditing(false)}
          onSaved={(data) => { setOrder(data); setEditing(false); }} />
      )}
    </div>
  );
}
